import { AnimatePresence, motion } from "framer-motion";
import { X, Lock, Truck, ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { api, eur, artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const COUNTRIES = ["US", "CA", "MX", "FR", "BE", "LU", "CH", "MC", "DE", "AT", "NL", "IT", "ES", "PT", "IE", "GB", "DK", "SE", "NO", "FI", "PL", "CZ", "GR", "AU", "NZ", "JP", "KR", "SG", "HK", "AE", "SA", "QA", "IL", "BR", "ZA", "MA"];
const EMPTY = { name: "", email: "", line1: "", line2: "", city: "", state: "", postal: "", country: "US", residential: true };
const inputCls = "w-full border border-bone/15 bg-ink px-4 py-3 font-mono text-[11px] tracking-[0.15em] text-bone placeholder:text-bone-muted/60 focus:border-brass/60 focus:outline-none";

export default function CheckoutModal() {
  const { cart, checkoutOpen, setCheckoutOpen } = useStore();
  const { t, lang } = useLang();
  const [addr, setAddr] = useState(EMPTY);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const subtotal = cart.reduce((s, i) => s + i.price, 0);
  const cartKey = cart.map((i) => i.artwork_id).sort().join(",");

  useEffect(() => setQuote(null), [cartKey]);

  const set = (k) => (e) => {
    setAddr((a) => ({ ...a, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    setQuote(null);
  };

  const getQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/shipping/quote", { items: cart.map((i) => ({ artwork_id: i.artwork_id })), address: addr });
      setQuote(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.quoteErr"));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: cart.map((i) => ({ artwork_id: i.artwork_id })),
        origin_url: window.location.origin,
        lang,
        quote_id: quote.quote_id,
        address: addr,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.toastErr"));
      setPaying(false);
      if (err.response?.status === 409) setQuote(null);
    }
  };

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <motion.div data-testid="checkout-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-ink/90 p-4 backdrop-blur-md">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="relative my-8 w-full max-w-2xl border border-bone/15 bg-ink-2 p-8 sm:p-10">
            <button data-testid="checkout-close-button" onClick={() => setCheckoutOpen(false)} className="absolute right-4 top-4 z-10 text-bone-muted transition-colors hover:text-brass" aria-label="Fermer">
              <X size={18} />
            </button>
            <p className="eyebrow">{t("checkout.eyebrow")}</p>

            <ul className="mt-8 space-y-2 border-b border-bone/10 pb-4">
              {cart.map((i) => (
                <li key={i.artwork_id} className="flex justify-between font-mono text-[11px] tracking-[0.1em] text-bone-dim">
                  <span>{artText(i, "title", lang)}</span>
                  <span className="text-bone">{eur(i.price)}</span>
                </li>
              ))}
            </ul>

            {!quote ? (
              <form data-testid="checkout-address-form" onSubmit={getQuote} className="mt-6">
                <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("checkout.addressTitle")}</p>
                <p className="mt-2 text-xs text-bone-dim/70">{t("checkout.addressHint")}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <input data-testid="checkout-name" required placeholder={t("checkout.namePh")} value={addr.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="checkout-email" required type="email" placeholder={t("checkout.emailPh")} value={addr.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="checkout-line1" required placeholder={t("checkout.addressPh")} value={addr.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} />
                  <input data-testid="checkout-line2" placeholder={t("checkout.address2Ph")} value={addr.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} />
                  <input data-testid="checkout-city" required placeholder={t("checkout.cityPh")} value={addr.city} onChange={set("city")} className={inputCls} />
                  <input data-testid="checkout-state" placeholder={t("checkout.statePh")} value={addr.state} onChange={set("state")} className={inputCls} />
                  <input data-testid="checkout-postal" required placeholder={t("checkout.postalPh")} value={addr.postal} onChange={set("postal")} className={inputCls} />
                  <select data-testid="checkout-country" value={addr.country} onChange={set("country")} className={inputCls}>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <label className="mt-4 flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-bone-dim">
                  <input data-testid="checkout-residential" type="checkbox" checked={addr.residential} onChange={set("residential")} className="accent-brass" />
                  {t("checkout.residential")}
                </label>
                <button data-testid="checkout-quote-button" type="submit" disabled={busy || cart.length === 0} className="mt-6 flex w-full items-center justify-center gap-3 border border-brass/60 bg-brass/10 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all hover:bg-brass hover:text-ink disabled:opacity-50">
                  <Truck size={13} /> {busy ? t("checkout.quoting") : t("checkout.getQuote")}
                </button>
              </form>
            ) : (
              <div data-testid="checkout-quote" className="mt-6">
                <button data-testid="checkout-edit-address" onClick={() => setQuote(null)} className="flex items-center gap-1 font-mono text-[9px] tracking-[0.25em] text-bone-muted transition-colors hover:text-brass">
                  <ChevronLeft size={12} /> {t("checkout.editAddress")}
                </button>
                <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-bone-dim">
                  {addr.name} · {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}, {addr.postal} {addr.city}{addr.state ? ` ${addr.state}` : ""}, {addr.country}
                </p>
                <div className="mt-5 border border-bone/10 bg-ink p-4">
                  <div className="flex justify-between font-mono text-[10px] tracking-[0.15em] text-bone-dim">
                    <span data-testid="checkout-shipping-service">{quote.fallback ? t("checkout.fallbackService") : `UPS — ${quote.service}`}</span>
                    <span>{eur(quote.shipping_eur)}</span>
                  </div>
                  {!quote.fallback && quote.ups_currency !== "EUR" && (
                    <p className="mt-1 font-mono text-[9px] tracking-[0.1em] text-bone-muted">
                      {quote.ups_amount.toFixed(2)} {quote.ups_currency} × {quote.fx_rate.toFixed(4)}
                    </p>
                  )}
                  <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.15em] text-bone-dim">
                    <span>{t("checkout.handling")}</span>
                    <span>{eur(quote.handling_fee)}</span>
                  </div>
                  <div className="mt-3 flex justify-between border-t border-bone/10 pt-3 font-mono text-[10px] tracking-[0.25em] text-bone">
                    <span>{t("checkout.shippingLabel")}</span>
                    <span data-testid="checkout-shipping">{eur(quote.total_eur)}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-between">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-bone-muted">{t("checkout.totalLabel")}</span>
                  <span data-testid="checkout-total" className="font-serif text-2xl text-bone">{eur(subtotal + quote.total_eur)}</span>
                </div>
                <p className="mt-6 text-xs leading-relaxed text-bone-dim/70">{t("checkout.stripeNote")}</p>
                <button data-testid="checkout-pay-button" onClick={pay} disabled={paying} className="mt-6 flex w-full items-center justify-center gap-3 border border-brass/60 bg-brass/10 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all hover:bg-brass hover:text-ink disabled:opacity-50">
                  <Lock size={13} /> {paying ? t("checkout.redirecting") : `${t("checkout.pay")} — ${eur(subtotal + quote.total_eur)}`}
                </button>
                <p className="mt-4 text-center font-mono text-[9px] tracking-[0.25em] text-bone-muted">{t("checkout.secured")}</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
